import { useSyncExternalStore } from 'react';

/**
 * Whether the single-key shortcuts are on: `g` then a letter, `?`, and `j`/`k` from anywhere on a
 * work-list page.
 *
 * WCAG 2.1.4 (Character Key Shortcuts, Level A) is why this exists. A shortcut made only of
 * character keys that fires without focus on a control has to be switchable off — chiefly for
 * speech-input users, whose dictation can arrive as letters while focus is not in a field, so
 * that `g` then `a` takes them off the page they were on. Keys scoped to a focused list (the
 * queue's `j`/`↵`) are exempt and stay on regardless; Ctrl+K is a modified key and stays too.
 *
 * PER BROWSER, NOT PER USER, the way the rail's collapsed state is: it is a fact about the input
 * method at this desk, and somebody who dictates on one machine and types on another wants it
 * different on each. On by default — the keys are the screen's speed for everybody else.
 */
export const STORAGE_KEY = 'promanage:single-key-shortcuts';

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    // Private browsing or storage disabled — no stored preference, so the default.
    return true;
  }
}

// Same tiny external-store shape as lib/railCollapse.ts: read by the shell and by a couple of
// pages, changed rarely, and outside the React tree so a route change cannot reset it.

let enabled = true;
let initialised = false;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function init() {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;
  enabled = readStored();

  // Kept in step across tabs: switching the keys off in one tab and having `g` still fire in
  // another is exactly the surprise the setting is there to prevent.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    enabled = event.newValue !== 'off';
    emit();
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

if (typeof window !== 'undefined') init();

/** For a listener reading it at keypress time, outside React. */
export function singleKeysEnabled(): boolean {
  return enabled;
}

export function setSingleKeysEnabled(next: boolean): void {
  enabled = next;
  try {
    localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
  } catch {
    // The preference will not persist; this session still honours it.
  }
  emit();
}

export function useSingleKeysEnabled(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => enabled,
    () => true,
  );
}
